import { Box, Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { type FC, useEffect } from 'react';

import { getTenantUnits } from '@core/configuration';
import type { ProjectFormikType, RequiredProjectFieldsInfo } from '@core/formik/project';
import { hasValue } from '@core/utils/equality';
import { getCurrencySymbol } from '@core/utils/format';
import { requiredLabel } from '@core/utils/strings';

import { Input } from '../../../../components/Input/Input';
import { useSlabQuery } from '../../../../hooks/useSlabQuery';

type Props = {
  requiredFields: RequiredProjectFieldsInfo;
};

export const DetailsSection: FC<Props> = ({ requiredFields }) => {
  const formikBag = useFormikContext<ProjectFormikType>();

  // The revenue override is project-level, so it uses the currency of the
  // project's first selected plant. Kept in sync as the selection changes.
  const firstPlantID = formikBag.values.productsByPlants?.[0]?.plant?.id ?? null;
  const { data: maybeFirstPlant, isLoading: isLoadingFirstPlant } = useSlabQuery(
    'GET plant by ID',
    { pathParams: { id: firstPlantID ?? '' } },
    { enabled: firstPlantID !== null },
  );
  const currencySymbol = getCurrencySymbol(
    firstPlantID === null ? null : maybeFirstPlant?.currencyCode,
  );

  // The override's currency is stamped on input change, which races the plant
  // lookup above (and goes stale when the plant selection changes). Re-sync the
  // stored code whenever the resolved plant currency changes.
  const resolvedCurrencyCode = firstPlantID === null ? null : maybeFirstPlant?.currencyCode;
  useEffect(() => {
    const override = formikBag.values.revenueOverride;
    if (!resolvedCurrencyCode || !override || override.currency === resolvedCurrencyCode) {
      return;
    }
    formikBag.setFieldValue('revenueOverride', { ...override, currency: resolvedCurrencyCode });
  }, [resolvedCurrencyCode, formikBag.values.revenueOverride?.currency]);

  return (
    <Box display='flex' flexDirection='column' gap='1rem'>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Input
            name='revenueOverride'
            label={requiredLabel('Revenue Override', requiredFields.revenueOverride)}
            type='currency'
            startAdornment={currencySymbol}
            disabled={isLoadingFirstPlant}
            onInputChange={async (val) => {
              if (val === '' || val === null) {
                formikBag.setFieldValue('revenueOverride', null);
                return;
              }

              await formikBag.setFieldValue('revenueOverride', {
                number: val,
                currency: resolvedCurrencyCode || 'USD',
              });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            name='readyMixQuantityOverride'
            label={`Ready Mix Quantity Override${requiredFields.readyMixQuantityOverride ? '*' : ''}`}
            type='number'
            onInputChange={async (val) => {
              formikBag.setFieldValue(
                'readyMixQuantityOverride',
                val === '' || val === null ? null : Number(val),
              );

              if (val === '' || val === null) {
                formikBag.setFieldValue('readyMixQuantityOverrideUnit', null);
              } else if (!formikBag.values.readyMixQuantityOverrideUnit) {
                formikBag.setFieldValue('readyMixQuantityOverrideUnit', getTenantUnits().dryVolume);
              }
            }}
            endAdornment={
              hasValue(formikBag.values.readyMixQuantityOverride)
                ? getTenantUnits().dryVolume
                : undefined
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};
